import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useMemo, useState, useCallback, useRef, useEffect, } from "react";
import "./graph-node.scss";
import PLUS_CIRCLE from "./../../../../../assets/icons/plus_circle_icon.png";
import GRAP_ARROW_RIGHT from "./../../../../../assets/images/graph_arrow_right.png";
import { IconCore } from "../../../../../core/coreComponents/IconCore/IconCore";
import { COLORS } from "../../../../../core/coreStyles/colors";
import { GraphNodeChip } from "./components/GraphNodeChip/GraphNodeChip";
import { TextCore } from "../../../../../core/coreComponents/TextCore/TextCore";
import { graphQueries } from "../../../controller/GraphController";
import { FlowContext } from "../../contexts/FlowContext";
import Tippy from "@tippyjs/react";
import { CategoryFieldsDropdownCore } from "../../../../../core/coreComponents/DropdownCore/CategoryFieldsDropdownCore/CategoryFieldsDropdownCore";
import { getCategoryLabel, getEntityLabel, } from "../../utils/idToLabelTranslator";
import X_ICON from "../../../../../assets/icons/x_icon.png";
import { EmptyGraphNode } from "./components/EmptyGraphNode/EmptyGraphNode";
const getNodeById = (id, flow) => flow.find((node) => node.id === id);
export const GraphNode = ({ theme, graphNodeData, onChange, nodeIndex = 0, }) => {
    const flowContext = useContext(FlowContext);
    const { flowState, setFlowState, setSelectedEntitiesSet, selectedEntitiesSet, } = flowContext || {};
    const [showSelectionMenu, setShowSelectionMenu] = useState(false);
    const containerRef = useRef(null);
    const handleAddNode = useCallback(() => {
        const currentFlow = flowState || [];
        const edgeNodeIndex = nodeIndex;
        const newFlow = graphQueries.addNode(edgeNodeIndex, [], currentFlow);
        setFlowState === null || setFlowState === void 0 ? void 0 : setFlowState(newFlow);
    }, [flowState, nodeIndex, setFlowState, graphQueries]);
    useEffect(() => {
        const updatedSelectedEntitySet = graphQueries.buildSelectedEntitiesSet(flowState);
        setSelectedEntitiesSet(updatedSelectedEntitySet);
    }, [flowState]);
    useEffect(() => {
        function handleClickOutsideSelectionMenu(event) {
            if (containerRef.current &&
                !containerRef.current.contains(event.target) &&
                showSelectionMenu) {
                setShowSelectionMenu(false);
            }
        }
        if (showSelectionMenu) {
            document.addEventListener("mousedown", handleClickOutsideSelectionMenu);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutsideSelectionMenu);
        };
    }, [showSelectionMenu]);
    const { selectedFieldsByCategory } = graphNodeData;
    const shouldShowPrecedingArrow = nodeIndex > 0;
    const flatOptionsAndStatus = useMemo(() => {
        if (!selectedFieldsByCategory) {
            return {
                flatOptions: [],
                isEmpty: true,
                sourceFieldId: undefined,
            };
        }
        let isNodeEmpty = true;
        let firstSourceId = undefined;
        const flatOptions = [];
        for (const [category, selectedFieldIds] of Object.entries(selectedFieldsByCategory)) {
            const categoryId = category;
            const ids = selectedFieldIds || [];
            for (const entityId of ids) {
                if (firstSourceId === undefined) {
                    firstSourceId = entityId;
                }
                isNodeEmpty = false;
                flatOptions.push({
                    categoryId: categoryId,
                    entityId: entityId,
                });
            }
        }
        return {
            flatOptions: flatOptions,
            isEmpty: isNodeEmpty,
            sourceFieldId: firstSourceId,
        };
    }, [graphNodeData]);
    const { flatOptions: flatCurrentNodeSelectedOptions, isEmpty, sourceFieldId, } = flatOptionsAndStatus;
    const hasMultipleValues = flatCurrentNodeSelectedOptions.length > 1;
    const precedingNode = flowState === null || flowState === void 0 ? void 0 : flowState[nodeIndex - 1];
    const precedingEntityId = graphQueries.getPrecedingSourceFieldId(precedingNode);
    const valuesByCategoryGetter = useCallback((selectedCategoryId) => {
        const isEmptyRootNode = nodeIndex === 0 && !Object.keys(selectedFieldsByCategory || {}).length;
        if (isEmptyRootNode || !precedingEntityId) {
            return graphQueries.getRawEntitiesByCategory(selectedCategoryId);
        }
        return graphQueries.getLegalEntityOptionsByCategory(precedingEntityId, selectedCategoryId, flowState, nodeIndex, selectedEntitiesSet, getEntityLabel);
    }, [
        graphQueries,
        nodeIndex,
        precedingEntityId,
        flowState,
        selectedEntitiesSet,
        selectedFieldsByCategory,
    ]);
    const categoryOptions = useMemo(() => {
        const availableCategoryIds = graphQueries.getLegalUnusedCategoriesForNewNode(flowState, nodeIndex, selectedEntitiesSet);
        return availableCategoryIds.map((categoryId) => ({
            id: categoryId,
            label: getCategoryLabel(categoryId),
            category: categoryId,
        }));
    }, [graphQueries, flowState, nodeIndex, selectedEntitiesSet]);
    const onFieldSelected = useCallback((newSelectedFieldsByCategory) => {
        var _a;
        const allSelectedIds = Object.values(newSelectedFieldsByCategory).flat();
        if (allSelectedIds.length === 0) {
            return;
        }
        const oldFlow = flowState || [];
        const currentNode = getNodeById(graphNodeData.id, oldFlow);
        if (!currentNode)
            return;
        const newNode = Object.assign(Object.assign({}, currentNode), { selectedFieldsByCategory: newSelectedFieldsByCategory });
        const editedFlow = graphQueries.editNode(newNode, oldFlow);
        const slicedFlow = graphQueries.deleteNode((_a = flowState[nodeIndex + 1]) === null || _a === void 0 ? void 0 : _a.id, editedFlow);
        onChange(slicedFlow);
        setShowSelectionMenu(false);
    }, [graphQueries, graphNodeData, flowState, onChange, nodeIndex]);
    const shouldShowNextButton = useMemo(() => {
        if (isEmpty ||
            hasMultipleValues ||
            nodeIndex !== ((flowState === null || flowState === void 0 ? void 0 : flowState.length) || 0) - 1) {
            return false;
        }
        if (!sourceFieldId) {
            return false;
        }
        return graphQueries.hasLegalUnusedNextEdges(sourceFieldId, flowState, nodeIndex, selectedEntitiesSet);
    }, [
        nodeIndex,
        flowState,
        isEmpty,
        hasMultipleValues,
        sourceFieldId,
        selectedEntitiesSet,
    ]);
    const handleDropdownOpen = useCallback(() => setShowSelectionMenu(true), []);
    return (_jsxs("div", { className: "flex gap-8 align-center", children: [shouldShowPrecedingArrow && (_jsx(IconCore, { src: GRAP_ARROW_RIGHT, width: 52, height: 28, containerStyle: { padding: 0 } })), _jsxs("div", { className: "flex gap-8 align-center flex-wrap graph-node-container", ref: containerRef, children: [isEmpty ? (_jsx(EmptyGraphNode, { onClick: handleDropdownOpen })) : (_jsxs("div", { onClick: handleDropdownOpen, className: "graph-node-field-chip-container graph-node-badge-content flex align-center", children: [flatCurrentNodeSelectedOptions.map((option, index) => (_jsxs(React.Fragment, { children: [index > 0 && (_jsx(TextCore, { text: "OR", fontWeight: "bold", color: COLORS["gray-9"] })), _jsx(GraphNodeChip, { categoryLabel: getCategoryLabel(option.categoryId), fieldLabel: getEntityLabel(option.entityId), theme: theme })] }, `${option.categoryId}-${option.entityId}`))), _jsx(IconCore, { src: X_ICON, className: "hover-blue-11", containerStyle: { borderLeft: "1px solid #D9D9D9" }, onClick: () => {
                                    const newFlow = graphQueries.deleteNode(graphNodeData.id, flowState);
                                    setFlowState === null || setFlowState === void 0 ? void 0 : setFlowState(newFlow);
                                } })] })), showSelectionMenu && (_jsx(CategoryFieldsDropdownCore, { className: "node-chip-dropdown fade-in", keyOptions: categoryOptions, categoriesLabelGetter: getCategoryLabel, valueOptionsLabelGetter: getEntityLabel, valueOptionsGetter: valuesByCategoryGetter, selectedOptionsMapping: graphNodeData.selectedFieldsByCategory, onChange: onFieldSelected }))] }), shouldShowNextButton && (_jsx(Tippy, { content: "Add data source", placement: "right-end", delay: [500, 0], children: _jsx("div", { className: "hover-blue-11 circle-button-container", onClick: handleAddNode, children: _jsx(IconCore, { src: PLUS_CIRCLE, width: 24, height: 24 }) }) }))] }));
};
